import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatDistanceStrict, parseISO } from "date-fns";
import DeleteFileDialog from "files/components/DeleteFileDialog";
import FileActionDialog from "files/components/FileActionDialog";
import useCanShareFile from "files/hooks/useCanShareFile";
import useFileAccess from "files/hooks/useFileAccess";
import { BASE_DIRECTORIES, resolveRouterPath } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { JobType } from "files/types/interfaces/fileJob";
import { Copy, File, Folder, MoreHorizontal, Trash2 } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { cn } from "~/lib/utils";
import { apiMutate } from "~/utils/apiClient";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { FileDownload } from "./file-download";
import { FileTitle } from "./file-title";
import { ShareLinkDialog } from "./share-link-dialog";
import { ShareLinksManifest } from "./share-links-manifest";
import useToast from "./toast";

const { VITE_RWB_API } = import.meta.env;

// Any folder whose object key looks like "projects/<id>[/]" is a project's own root — shown
// under the file browser but owned/managed by the project, so it can't be renamed or removed
// from here.
export const PROJECT_PREFIX_REGEX = new RegExp(`^${BaseDirectory.PROJECTS}/\\d+/?$`);

const formatRelativeDate = (iso: string): string => {
  try {
    return formatDistanceStrict(parseISO(iso), new Date(), { addSuffix: true });
  } catch {
    return "";
  }
};

interface FileListingRowProps {
  node: FileInfo;
  href?: string;
  disabled?: boolean;
  /**
   * "link" navigates via the app router (the default file-browser experience).
   * "callback" navigates via `onNavigate`/`onPreview` instead, for listings that
   * track the current folder as local state rather than the URL.
   */
  navigation: "link" | "callback";
  /** Hides the row's "..." actions menu (rename/move/delete/share/etc.) regardless of write permission. */
  hideActions?: boolean;
  onNavigate?: () => void;
  onPreview?: () => void;
  /**
   * Explicit directory prefix to revalidate after rename/move/delete. Pass this for
   * listings (like PersonalDatasetFiles) that track the browsed folder as local state
   * rather than the URL, since the shared dialogs otherwise resolve the current
   * directory from the route.
   */
  mutatePrefix?: string;
}

// Shared by DatasetFiles.tsx and PersonalDatasetFiles.tsx so both Files tabs
// render identical rows, including write actions — write access is granted
// per-file-key (see useFileAccess), not scoped to a particular dataset.
export const FileListingRow = ({
  node,
  href,
  disabled,
  navigation,
  hideActions,
  onNavigate,
  onPreview,
  mutatePrefix,
}: FileListingRowProps) => {
  const isDir = node.type === FileType.DIRECTORY;
  const isDisabled = !isDir && disabled;
  const name = node.key.split("/").filter(Boolean).pop() ?? node.key;
  const location = useLocation();
  const { showSuccess } = useToast();
  const canModify = useFileAccess(node.key, "write");
  const [fileAction, setFileAction] = useState<JobType | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [shareLinkUrl, setShareLinkUrl] = useState<string | null>(null);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);
  const [shareLinkError, setShareLinkError] = useState(false);
  const [shareLinksOpen, setShareLinksOpen] = useState(false);
  const canShareFile = useCanShareFile(node.key);

  const showActions =
    !hideActions &&
    !node.pending_action &&
    !BASE_DIRECTORIES[resolveRouterPath(node.key)] &&
    !PROJECT_PREFIX_REGEX.test(node.key);

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    navigator.clipboard.writeText(`${window.location.origin}${href}`);
    showSuccess("Link copied to clipboard");
  };

  const handleCopyS3Uri = (e: React.MouseEvent) => {
    e.preventDefault();
    if (node.s3_uri) {
      navigator.clipboard.writeText(node.s3_uri);
      showSuccess("Cloud path copied to clipboard");
    }
  };

  const handleGenerateShareLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    setShareLinkUrl(null);
    setShareLinkError(false);
    setShareLinkLoading(true);
    setShareLinkOpen(true);
    try {
      const url = await apiMutate<string>(
        `${VITE_RWB_API}/file-browser/generate-download-url`,
        "POST",
        { key: node.key }
      );
      setShareLinkUrl(url);
    } catch {
      setShareLinkError(true);
    } finally {
      setShareLinkLoading(false);
    }
  };

  const linkTo = isDir ? href : { pathname: location.pathname, search: location.search };
  const linkState = isDir ? undefined : { preview: { key: node.key, size: node.size } };

  const rowIcon = isDir ? (
    <Folder className="size-4 shrink-0 text-primary" aria-hidden="true" />
  ) : (
    <File className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  );

  return (
    <>
      <div
        className={cn(
          "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
          !isDisabled && "hover:bg-muted"
        )}
      >
        {navigation === "callback" ? (
          <button
            type="button"
            disabled={isDisabled}
            onClick={isDir ? onNavigate : onPreview}
            className={cn(
              "flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left",
              isDisabled && "cursor-default opacity-50 pointer-events-none"
            )}
          >
            {rowIcon}
            <span className="flex-1 truncate text-foreground">
              <FileTitle fileKey={node.key} />
            </span>
          </button>
        ) : (
          <Link
            to={isDisabled ? location.pathname : (linkTo ?? "")}
            state={isDisabled ? undefined : linkState}
            replace={!isDir}
            className={cn(
              "flex flex-1 min-w-0 items-center gap-3",
              isDisabled && "cursor-default opacity-50 pointer-events-none"
            )}
          >
            {rowIcon}
            <span className="flex-1 truncate text-foreground">
              <FileTitle fileKey={node.key} />
            </span>
          </Link>
        )}
        <span className="w-20 text-right text-xs text-muted-foreground">
          {!isDir && node.size != null ? prettyBytes(node.size) : ""}
        </span>
        <span className="w-32 text-right text-xs text-muted-foreground">
          {node.updated_at ? formatRelativeDate(node.updated_at) : ""}
        </span>
        {showActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-foreground/10"
                aria-label={`Actions for ${name}`}
                onClick={(e) => e.preventDefault()}
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
              {!isDir && (
                <FileDownload fileKey={node.key}>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>Download</DropdownMenuItem>
                </FileDownload>
              )}
              {!isDir && canShareFile && (
                <DropdownMenuItem onClick={handleGenerateShareLink}>
                  Generate share link
                </DropdownMenuItem>
              )}
              {isDir && canShareFile && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    setShareLinksOpen(true);
                  }}
                >
                  Generate share links
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleCopyLink}>
                <Copy />
                Copy link
              </DropdownMenuItem>
              {node.s3_uri && (
                <DropdownMenuItem onClick={handleCopyS3Uri}>
                  <Copy />
                  Copy S3 URI
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => setFileAction(JobType.COPY)}>
                Copy {isDir ? "Folder" : "File"}...
              </DropdownMenuItem>
              {canModify && (
                <DropdownMenuItem onClick={() => setFileAction(JobType.RENAME)}>
                  Rename...
                </DropdownMenuItem>
              )}
              {canModify && (
                <DropdownMenuItem onClick={() => setFileAction(JobType.MOVE)}>
                  Move...
                </DropdownMenuItem>
              )}
              {canModify && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="w-7" />
        )}
      </div>
      {fileAction && (
        <FileActionDialog
          open
          fileKey={node.key}
          action={fileAction}
          isDirectory={isDir}
          handleClose={() => setFileAction(null)}
          prefix={mutatePrefix}
        />
      )}
      <DeleteFileDialog
        open={deleteOpen}
        fileKey={node.key}
        isDirectory={isDir}
        handleClose={() => setDeleteOpen(false)}
        prefix={mutatePrefix}
      />
      <ShareLinkDialog
        url={shareLinkUrl}
        loading={shareLinkLoading}
        error={shareLinkError}
        open={shareLinkOpen}
        onOpenChange={setShareLinkOpen}
      />
      <ShareLinksManifest
        fileKey={node.key}
        open={shareLinksOpen}
        onOpenChange={setShareLinksOpen}
      />
    </>
  );
};

interface FileListingBackRowProps {
  href?: string;
  onClick?: () => void;
}

export const FileListingBackRow = ({ href, onClick }: FileListingBackRowProps) => {
  const className = cn(
    "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm",
    "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  );
  const content = (
    <>
      <Folder className="size-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="flex-1 text-muted-foreground">..</span>
      <span className="w-20" />
      <span className="w-32" />
      <span className="w-7" />
    </>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, "w-full cursor-pointer text-left")}
    >
      {content}
    </button>
  ) : (
    <Link to={href ?? ""} className={className}>
      {content}
    </Link>
  );
};
